import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import SectionParticles from './SectionParticles';

const ProjectsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="projects" 
      className="py-24 md:py-32 relative overflow-hidden" 
      ref={ref} 
      data-testid="projects-section"
      style={{ background: '#050505' }}
    >
      <SectionParticles />
      <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Projects
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        {/* The Celestial Portfolio */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 
            className="text-2xl md:text-3xl font-light tracking-wider text-[#c9a227] mb-8 text-center"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            The Celestial Portfolio
          </h3>
          <div className="space-y-6 text-white/70 leading-relaxed">
            <p>
              The projects presented here represent active, conceptual, or exploratory initiatives developed within Celestial Minds™. Some projects are internal. Others are selectively introduced through private briefings or strategic partnerships.
            </p>
            <p className="text-[#c9a227]/80 italic">
              Not all projects are publicly disclosed.
            </p>
            <p>
              Every project we develop is designed with the precision of science, the imagination of futurism, and the executional potential required by sovereign-level investors.
            </p>
            <p>
              Our work is organised across several core domains of exploration and system design.
            </p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* 1. Strategic Intelligence & Growth Architecture */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-white mb-2"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            1. Strategic Intelligence & Growth Architecture
          </h3>
          <p className="text-[#c9a227] text-sm tracking-wider mb-6">A Celestial Minds™ Project Division</p>
          
          <div className="space-y-6 text-white/70 leading-relaxed">
            <p>
              Strategic Intelligence & Growth Architecture is one of several project divisions within the Celestial Minds™ ecosystem.
            </p>
            <p>
              It exists to support organisations that recognise their next stage of growth will not be achieved through optimisation, automation, or tools alone — but through a deeper re-examination of how intelligence itself is structured within the enterprise.
            </p>
            <p>
              Within this division, Celestial Minds™ applies advanced intelligence and AI capabilities at a fundamentally different level: designing how organisations think, decide, and scale over time.
            </p>
            <p>
              This work sits above conventional consulting and beyond standard AI adoption. It is offered selectively as part of the broader Celestial Minds™ mandate.
            </p>
            <p className="text-[#c9a227] italic text-lg">
              We redesign how companies think — not just how they operate.
            </p>
          </div>

          {/* Sub-sections */}
          <div className="mt-10 space-y-10">
            {/* A — What We Deliver */}
            <div className="pl-6 border-l-2 border-[#c9a227]/30">
              <h4 className="text-lg text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>A — What We Deliver</h4>
              <div className="space-y-4 text-white/70">
                <p>Most organisations use AI to improve efficiency.</p>
                <p>Within this division, Celestial Minds™ applies intelligence to re-architect the business itself.</p>
                <p>By combining AI-augmented reasoning with deep behavioural and systems analysis, we surface growth pathways that remain invisible to traditional strategy, data analysis, and optimisation frameworks.</p>
                <p className="text-white/90 italic">This is not about adding intelligence to existing systems — it is about changing the intelligence that governs the system.</p>
                <p>Our work operates at the intersection of human behaviour, systems intelligence, and long-range economic design.</p>
              </div>
            </div>

            {/* B — Strategic Focus */}
            <div className="pl-6 border-l-2 border-[#c9a227]/30">
              <h4 className="text-lg text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>B — Strategic Focus</h4>
              <p className="text-white/70 mb-4">Through this project division, organisations engage in work that enables them to:</p>
              <ul className="space-y-2 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-[#c9a227] mt-1">•</span>
                  <span>Redesign internal intelligence structures to unlock new revenue potential</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c9a227] mt-1">•</span>
                  <span>Build scalability models that remain resilient, adaptive, and self-reinforcing</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c9a227] mt-1">•</span>
                  <span>Align leadership behaviour, incentives, and decision-making at a systemic level</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c9a227] mt-1">•</span>
                  <span>Engineer long-range growth trajectories measured in decades, not quarters</span>
                </li>
              </ul>
              <p className="text-white/70 mt-4">This approach applies across industries and remains relevant regardless of how advanced an organisation's existing AI capabilities may be.</p>
            </div>

            {/* C — A Deliberate Invitation */}
            <div className="pl-6 border-l-2 border-[#c9a227]/30">
              <h4 className="text-lg text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>C — A Deliberate Invitation</h4>
              <div className="space-y-4 text-white/70">
                <p>Strategic Intelligence & Growth Architecture is not positioned as a general consultancy offering, nor as the primary focus of Celestial Minds™.</p>
                <p>It is made available to leadership teams who recognise that enduring advantage emerges when intelligence becomes a design discipline rather than an afterthought.</p>
                <p>If you are exploring how far your organisation can scale when the way it thinks is intentionally engineered, we welcome a considered conversation.</p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* 2. Advanced Technologies & Human Enhancement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-white mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            2. Advanced Technologies & Human Enhancement
          </h3>
          <p className="text-white/70 mb-6">Systems crafted for the next iteration of human capability.</p>
          <ul className="space-y-3 text-white/70">
            {[
              'AI consciousness frameworks and emergent-intelligence models',
              'Quantum-era toolsets for navigation, simulation, and prediction',
              'Neural-inspired interfaces for human–machine co-evolution',
              'Human augmentation philosophies grounded in ethics and longevity',
              'Large-scale economic superstructures',
              'Continental-scale logistical redesigns',
              'Developmental uplift architectures for emerging nations',
              'Quantum–AI entanglement ecosystems',
              'Intellectual frameworks for the coming era of hyper-mind intelligence'
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#c9a227] italic mt-6">These are not ideas — they are foundations for a new species-level trajectory.</p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* 3. Planetary Infrastructure & Future Cities */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-white mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            3. Planetary Infrastructure & Future Cities
          </h3>
          <p className="text-white/70 mb-6">Designs that redefine how civilisations grow, connect, and flourish.</p>
          <ul className="space-y-3 text-white/70">
            {[
              'Smart civilisational ecosystems',
              'Interconnected intelligent habitats',
              'Autonomous city clusters',
              'Post-scarcity architectural models',
              'Energy and resource systems built for circular abundance'
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-white/70 italic mt-6">Each concept is crafted for governments, visionary developers, and partner institutions capable of reshaping entire nations.</p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* 4. Education, Justice & Intellectual Renaissance */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-white mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            4. Education, Justice & Intellectual Renaissance
          </h3>
          <p className="text-white/70 mb-6">Blueprints to elevate how society learns, thinks, and governs itself.</p>
          <p className="text-white/70 mb-4">A radically new model of learning: intuitive, adaptive, cognitive, and deeply human</p>
          <p className="text-[#c9a227] italic">We design frameworks that uplift humanity intellectually, emotionally, and spiritually — at scale.</p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* 5. Ethical Governance for a New Era */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-white mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            5. Ethical Governance for a New Era
          </h3>
          <p className="text-white/70 mb-6">Governance systems that embrace wisdom over power.</p>
          <ul className="space-y-3 text-white/70">
            {[
              'Transparent decision architectures',
              'Systems that prioritise clarity, integrity, and truth',
              'Structures resilient against corruption and systemic failure',
              'New-era policy frameworks grounded in intelligence and humanity'
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#c9a227] italic mt-6">This is governance for the civilisation we could be — not the one we are.</p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />

        {/* 6. Future Thought Experiments & Civilisation Design */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-white mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            6. Future Thought Experiments & Civilisation Design
          </h3>
          <p className="text-white/70 mb-6">Where science, philosophy, and unprecedented imagination converge.</p>
          <ul className="space-y-3 text-white/70">
            {[
              'Macro-civilisational blueprints',
              'Reality architecture and simulation-driven design',
              'Quantum possibility frameworks',
              'Fintech megastructures and sovereign-level economic models',
              'Nation-building concepts engineered for long-term stability'
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-[#c9a227] italic text-lg mt-8 text-center">
            Celestial Minds™ is where the next reality is conceived — and prepared for those bold enough to build it.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsSection;
